import { Fragment, useMemo, useState } from 'react';
import {
  useProjectCategoryOptions,
  useCreateProjectCategoryOption,
  useUpdateProjectCategoryOption,
  useUpdateCategoryDefaultSections,
  useDeleteProjectCategoryOption,
  useTaxEngineStatus,
} from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Pencil, Archive, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import type { ProjectCategoryOption } from '@/lib/schemas';
import { PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH } from '@/lib/schemas';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/lib/auth/AuthProvider';
import { ADDON_SECTIONS, SECTION_METADATA } from '@/config/sectionMetadata';
import {
  TAX_CLASS_OPTIONS,
  TAX_CLASS_UNSET,
  taxClassLabel,
} from '@/lib/taxClasses';
import { TaxClassInfoTooltip } from '@/components/invoices/TaxClassInfoTooltip';

interface CategoryFormState {
  value: string;
  displayOrder: string;
  isActive: boolean;
  numberPrefix: string;
}

const defaultFormState: CategoryFormState = {
  value: '',
  displayOrder: '',
  isActive: true,
  numberPrefix: '',
};

export function ProjectCategoriesTab() {
  const { data: categories, isLoading } = useProjectCategoryOptions(true);
  const createCategory = useCreateProjectCategoryOption();
  const updateCategory = useUpdateProjectCategoryOption();
  const updateDefaultSections = useUpdateCategoryDefaultSections();
  const deleteCategory = useDeleteProjectCategoryOption();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  // v0.17 taxclass-label fix: same fail-closed context as InvoiceItemRow, so
  // this selector's options never claim "GST + BC PST" when it isn't live.
  const { data: taxEngineStatus } = useTaxEngineStatus();
  const taxEngineContext = taxEngineStatus
    ? { tax_engine: taxEngineStatus.tax_engine, rules: taxEngineStatus.rules }
    : undefined;

  // R4: Toggle client_required on a category (admin-only)
  const handleToggleClientRequired = async (category: ProjectCategoryOption, next: boolean) => {
    await updateCategory.mutateAsync({
      id: category.id,
      data: { client_required: next } as any,
    });
  };

  // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC28): change a category's
  // default_tax_class -- the architectural/liquor mapping is CONFIGURATION
  // Christa can edit, not a constant a developer ships. Applies only to
  // items created afterwards; existing items keep the class they carry.
  const handleDefaultTaxClassChange = async (
    category: ProjectCategoryOption,
    next: string,
  ) => {
    await updateCategory.mutateAsync({
      id: category.id,
      data: { default_tax_class: next === TAX_CLASS_UNSET ? null : next },
    });
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProjectCategoryOption | null>(null);
  const [formState, setFormState] = useState<CategoryFormState>(defaultFormState);

  // 2026-09-13: number_prefix must agree with the backend's max_length=10
  // (schemas/project_categories.py) — see PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH.
  // Deliberately NOT enforced via an <Input maxLength> attribute: that silently
  // drops keystrokes past the cap with no visible feedback, which is exactly how
  // "E2EV17" saved as "E2EV1" with no error shown. Instead the field accepts
  // whatever is typed and this derived flag drives a visible counter/error and
  // blocks submission.
  const trimmedNumberPrefixLength = formState.numberPrefix.trim().length;
  const numberPrefixTooLong = trimmedNumberPrefixLength > PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH;

  // R4: Default modules inline expand state
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [pendingSections, setPendingSections] = useState<string[]>([]);

  const sortedCategories = useMemo(() => {
    if (!categories) return [];
    return [...categories].sort((a, b) => {
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1;
      }
      return a.display_order - b.display_order;
    });
  }, [categories]);

  const openCreateDialog = () => {
    setEditingCategory(null);
    setFormState(defaultFormState);
    setIsDialogOpen(true);
  };

  const openEditDialog = (category: ProjectCategoryOption) => {
    setEditingCategory(category);
    setFormState({
      value: category.value,
      displayOrder: category.display_order?.toString() ?? '',
      isActive: category.is_active,
      numberPrefix: category.number_prefix ?? '',
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingCategory(null);
    setFormState(defaultFormState);
  };

  const handleSubmit = async () => {
    if (!formState.value.trim()) {
      toast.error('Category name is required');
      return;
    }

    const trimmedPrefix = formState.numberPrefix.trim().toUpperCase();
    if (trimmedPrefix.length > PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH) {
      toast.error(
        `Number prefix must be ${PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH} characters or fewer (currently ${trimmedPrefix.length})`,
      );
      return;
    }

    const payload = {
      value: formState.value.trim(),
      display_order: formState.displayOrder ? Number(formState.displayOrder) : undefined,
      is_active: formState.isActive,
      number_prefix: trimmedPrefix || null,
    };

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          data: payload,
        });
      } else {
        await createCategory.mutateAsync(payload);
      }
      closeDialog();
    } catch (error) {
      // toast handled in hooks
    }
  };

  const handleArchive = async (category: ProjectCategoryOption) => {
    await deleteCategory.mutateAsync(category.id);
  };

  const handleRestore = async (category: ProjectCategoryOption) => {
    await updateCategory.mutateAsync({
      id: category.id,
      data: { is_active: true },
    });
  };

  // R4: Default modules inline expand handlers
  const handleToggleExpand = (category: ProjectCategoryOption) => {
    if (expandedCategoryId === category.id) {
      setExpandedCategoryId(null);
      return;
    }
    setExpandedCategoryId(category.id);
    // null default_sections means all add-ins enabled
    setPendingSections(
      category.default_sections != null
        ? [...category.default_sections]
        : [...ADDON_SECTIONS],
    );
  };

  const handleSectionToggle = (section: string, checked: boolean) => {
    setPendingSections((prev) =>
      checked ? [...prev, section] : prev.filter((s) => s !== section),
    );
  };

  const handleSaveDefaultSections = async (category: ProjectCategoryOption) => {
    try {
      await updateDefaultSections.mutateAsync({
        id: category.id,
        defaultSections: pendingSections,
      });
      setExpandedCategoryId(null);
    } catch {
      // toast handled in hook
    }
  };

  const getModulesDisplay = (category: ProjectCategoryOption): string => {
    if (
      category.default_sections == null ||
      category.default_sections.length === ADDON_SECTIONS.length
    ) {
      return 'All sections';
    }
    return `${category.default_sections.length} of ${ADDON_SECTIONS.length} optional`;
  };

  // Total column count for expanded row colSpan
  const columnCount = isAdmin ? 9 : 8;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, idx) => (
          <Skeleton key={idx} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Project Categories</h2>
          <p className="text-sm text-gray-600">
            Configure the dropdown used to tag and filter projects
          </p>
        </div>
        <Button onClick={openCreateDialog} className="inline-flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Category
        </Button>
      </div>

      {sortedCategories.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <p className="text-gray-700 font-medium">No categories yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add categories to tag and filter your projects.</p>
          <Button onClick={openCreateDialog} className="mt-4 inline-flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Category
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Next Number</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && (
                  <TableHead className="text-center" title="Require a client when creating a project in this category">
                    Require Client
                  </TableHead>
                )}
                <TableHead
                  title="Default tax class for NEW billable items on projects in this category. Existing items keep the class they carry."
                >
                  <span className="inline-flex items-center gap-1">
                    Default Tax Class
                    <TaxClassInfoTooltip context={taxEngineContext} testId="default-tax-class-info-icon" />
                  </span>
                </TableHead>
                <TableHead>Default Sections</TableHead>
                <TableHead className="text-right">Display Order</TableHead>
                <TableHead className="text-right w-48">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedCategories.map((category) => (
                <Fragment key={category.id}>
                  <TableRow className={!category.is_active ? 'opacity-70' : undefined}>
                    <TableCell className="font-medium">
                      {category.value}
                      {!category.is_active && (
                        <span className="ml-2 text-xs text-muted-foreground italic">(inactive)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {category.number_prefix ? (
                        <span className="font-mono">{category.number_prefix}</span>
                      ) : (
                        <span className="text-muted-foreground">(none)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">
                        {category.number_prefix
                          ? `${category.number_prefix}-${category.next_project_number ?? 1}`
                          : String(category.next_project_number ?? 1)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {category.is_active ? (
                        <Badge variant="secondary">Active</Badge>
                      ) : (
                        <Badge variant="outline">Archived</Badge>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-center">
                        <Checkbox
                          checked={(category as any).client_required ?? false}
                          disabled={updateCategory.isPending}
                          onCheckedChange={(checked) => handleToggleClientRequired(category, !!checked)}
                          aria-label={`Require client for ${category.value}`}
                          data-testid={`client-required-toggle-${category.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <select
                        value={category.default_tax_class ?? TAX_CLASS_UNSET}
                        onChange={(e) => handleDefaultTaxClassChange(category, e.target.value)}
                        disabled={updateCategory.isPending}
                        aria-label={`Default tax class for ${category.value}`}
                        data-testid={`default-tax-class-select-${category.id}`}
                        className="h-8 w-56 rounded-md border border-input bg-background px-2 text-sm"
                        title="Applies to NEW items on this category's projects only"
                      >
                        <option value={TAX_CLASS_UNSET}>Unclassified</option>
                        {TAX_CLASS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {!category.default_tax_class && (
                        <p className="mt-1 text-xs text-muted-foreground" data-testid={`default-tax-class-hint-${category.id}`}>
                          New items are unclassified
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <button
                          onClick={() => handleToggleExpand(category)}
                          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          aria-expanded={expandedCategoryId === category.id}
                          aria-label={`Configure default sections for ${category.value}`}
                          data-testid={`default-modules-toggle-${category.id}`}
                        >
                          <span>{getModulesDisplay(category)}</span>
                          {expandedCategoryId === category.id ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : (
                        <span
                          className="text-sm text-muted-foreground"
                          data-testid={`default-modules-display-${category.id}`}
                        >
                          {getModulesDisplay(category)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{category.display_order}</TableCell>
                    <TableCell className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(category)}
                        className="inline-flex items-center gap-1.5"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                      {category.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleArchive(category)}
                          className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50"
                        >
                          <Archive className="h-3.5 w-3.5" />
                          Archive
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestore(category)}
                          className="inline-flex items-center gap-1.5 text-emerald-600 hover:bg-green-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* R4: Default modules inline expand panel */}
                  {expandedCategoryId === category.id && (
                    <TableRow>
                      <TableCell colSpan={columnCount} className="bg-muted/30 px-6 py-4">
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-3">
                              Default sections for <span className="font-semibold">{category.value}</span> projects
                            </p>
                            <div
                              className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                              data-testid="default-modules-picker"
                            >
                              {ADDON_SECTIONS.map((section) => {
                                const meta = SECTION_METADATA[section];
                                const isChecked = pendingSections.includes(section);
                                return (
                                  <label
                                    key={section}
                                    className="flex items-center gap-2 cursor-pointer rounded-md border bg-background px-3 py-2 text-sm hover:bg-accent transition-colors"
                                  >
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(checked) =>
                                        handleSectionToggle(section, !!checked)
                                      }
                                      aria-label={meta?.label ?? section}
                                      data-testid={`module-checkbox-${section}`}
                                    />
                                    <span>{meta?.label ?? section}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Sets the default sections for <span className="font-medium">{category.value}</span>{' '}
                            projects. Applies to every <span className="font-medium">{category.value}</span> project
                            except ones you&apos;ve individually customized — those keep your choices. Turning a
                            section off hides it from the page but never deletes data.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            To turn a feature off for your whole account instead, use{' '}
                            <span className="font-medium">Settings → Modules</span>.
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveDefaultSections(category)}
                              disabled={updateDefaultSections.isPending}
                              data-testid="save-default-modules"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedCategoryId(null)}
                              data-testid="cancel-default-modules"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="category-name">Name</Label>
              <Input
                id="category-name"
                value={formState.value}
                onChange={(e) => setFormState((prev) => ({ ...prev, value: e.target.value }))}
                placeholder="e.g., Residential"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display-order">Display Order</Label>
              <Input
                id="display-order"
                type="number"
                value={formState.displayOrder}
                onChange={(e) => setFormState((prev) => ({ ...prev, displayOrder: e.target.value }))}
                placeholder="Auto"
                min={0}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="number-prefix">Number Prefix</Label>
              <Input
                id="number-prefix"
                value={formState.numberPrefix}
                onChange={(e) => setFormState((prev) => ({ ...prev, numberPrefix: e.target.value.toUpperCase() }))}
                placeholder="e.g., CHR"
                aria-invalid={numberPrefixTooLong}
              />
              <p className={numberPrefixTooLong ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                {numberPrefixTooLong
                  ? `Number prefix must be ${PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH} characters or fewer (currently ${trimmedNumberPrefixLength}).`
                  : `Projects will be numbered like: ${formState.numberPrefix || 'X'}-1, ${formState.numberPrefix || 'X'}-2, etc. (${trimmedNumberPrefixLength}/${PROJECT_CATEGORY_NUMBER_PREFIX_MAX_LENGTH})`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createCategory.isPending || updateCategory.isPending || numberPrefixTooLong}
            >
              {editingCategory ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
